import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase, SUPABASE_URL } from "@/integrations/supabase/client";
import { usePageTitle } from "@/hooks/usePageTitle";

import { useTranslation } from "react-i18next";
import { useAuth } from "@/providers/AuthProvider";

// UI Components
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

// Icons
import { Building, ArrowRight, ChevronRight, ChevronLeft, Save, Eye, EyeOff, Check, CreditCard, Sparkles, Shield, Zap, Users, CheckCircle } from "lucide-react";
import RegistrationSetupForm from "@/components/admin/RegistrationSetupForm";
import { PlanSelection } from "@/components/admin/PlanSelection";
import LanguageSelector from "@/components/LanguageSelector";
import { TooltipProvider } from "@/components/ui/tooltip";

// Stripe Components
import {Elements} from '@stripe/react-stripe-js';
import {loadStripe} from '@stripe/stripe-js';
import {PUBLISHABLE_KEY} from "@/integrations/stripe/key";

// Steps for registration
enum RegistrationStep {
    OrganizationDetails = 0,
    PlanSelection = 1,
    PaymentMethod = 2,
    UserDetails = 3,
}

// Local storage keys for persisting form data
const STORAGE_KEYS = {
    ORGANIZATION_FORM: 'digger_registration_organization',
    PLAN_SELECTION: 'digger_registration_plan',
    ATTENTION_MANUALLY_EDITED: 'digger_registration_attention_edited',
    CURRENT_STEP: 'digger_registration_step'
};

// Helper functions for localStorage persistence
const saveToStorage = (key: string, data: any) => {
	try {
		localStorage.setItem(key, JSON.stringify(data));
	} catch (error) {
		console.warn('Failed to save to localStorage:', error);
	}
};

const loadFromStorage = (key: string, defaultValue: any = null) => {
	try {
		const stored = localStorage.getItem(key);
		return stored ? JSON.parse(stored) : defaultValue;
	} catch (error) {
		console.warn('Failed to load from localStorage:', error);
		return defaultValue;
	}
};

const clearRegistrationStorage = () => {
	Object.values(STORAGE_KEYS).forEach(key => {
		try {
			localStorage.removeItem(key);
		} catch (error) {
			console.warn('Failed to clear localStorage:', error);
		}
	});
};

// Interface for Stripe Plan from API
interface StripePlan {
	product_id: string;
	product_name: string;
	description: string | null;
	price_id: string;
	currency: string;
	unit_amount: number;
	product_attrs: {
		id: string;
		name: string;
		type: string;
		active: boolean;
		description: string | null;
		default_price: string;
	};
	price_attrs: {
		id: string;
		type: string;
		active: boolean;
		currency: string;
		recurring: {
			interval: string;
			interval_count: number;
			usage_type: string;
		};
		lookup_key: string;
		unit_amount: number;
		unit_amount_decimal: string;
	};
}

const stripePromise = loadStripe(PUBLISHABLE_KEY);

const RegisterPage = () => {
	usePageTitle("Register");
	const navigate = useNavigate();
	const { t, i18n } = useTranslation();
    const { user } = useAuth();
    const [searchParams] = useSearchParams();    const [currentStep, setCurrentStep] = useState<RegistrationStep>(() => 
        loadFromStorage(STORAGE_KEYS.CURRENT_STEP, RegistrationStep.OrganizationDetails)
    );
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [attentionManuallyEdited, setAttentionManuallyEdited] = useState(() =>
        loadFromStorage(STORAGE_KEYS.ATTENTION_MANUALLY_EDITED, false)
    );const [billingInterval, setBillingInterval] = useState<'month' | 'year'>('year');    const [selectedPlan, setSelectedPlan] = useState<'tier1' | 'tier2'>(() => {
        const saved = loadFromStorage(STORAGE_KEYS.PLAN_SELECTION);
        return saved?.selectedPlan || 'tier1';
    });
    const [selectedPriceId, setSelectedPriceId] = useState<string>(() => {
        const saved = loadFromStorage(STORAGE_KEYS.PLAN_SELECTION);
        return saved?.selectedPriceId || '';
    });
    const [discountCode, setDiscountCode] = useState<string>(() => {
        const saved = loadFromStorage(STORAGE_KEYS.PLAN_SELECTION);
        return saved?.discountCode || '';
    });
    const [setupIntentId, setSetupIntentId] = useState<string | null>(null);    const [setupIntentClientSecret, setSetupIntentClientSecret] = useState<string | null>(null);
    const [paymentMethodCreated, setPaymentMethodCreated] = useState(false);
    const [isCreatingSetupIntent, setIsCreatingSetupIntent] = useState(false);
    
    // State for managing available plans from API
    const [availablePlans, setAvailablePlans] = useState<StripePlan[]>([]);
    const [isLoadingPlans, setIsLoadingPlans] = useState(false);
    // Handle URL parameter for language setting
    useEffect(() => {
        const langParam = searchParams.get('lang');
        if (langParam) {
            // Validate that the language is supported
            const supportedLanguages = ['en', 'fr', 'nl', 'es', 'de'];
            if (supportedLanguages.includes(langParam.toLowerCase())) {
                const langCode = langParam.toLowerCase();
                // Only change language if different from current to avoid unnecessary reloads
                if (i18n.language !== langCode) {
                    i18n.changeLanguage(langCode);
                }
            }
        }
    }, [searchParams, i18n]);

    // Handle URL parameters to populate form fields (excluding password and payment info)
    useEffect(() => {
        // Organization form parameters
        const orgParams = {
            name: searchParams.get('orgName') || searchParams.get('organizationName'),
            vatNumber: searchParams.get('vatNumber') || searchParams.get('vat'),
            invoiceEmail: searchParams.get('invoiceEmail') || searchParams.get('billingEmail'),
            attention: searchParams.get('attention'),
            street: searchParams.get('street') || searchParams.get('address'),
            city: searchParams.get('city'),
            zip: searchParams.get('zip') || searchParams.get('zipCode') || searchParams.get('postalCode'),
            country: searchParams.get('country')
        };

        // User form parameters (excluding password and confirmPassword)
        const userParams = {
            firstName: searchParams.get('firstName') || searchParams.get('fname'),
            lastName: searchParams.get('lastName') || searchParams.get('lname'),
            email: searchParams.get('email')
        };        // Update organization form if any parameters are provided
        const hasOrgParams = Object.values(orgParams).some(value => value !== null);
        if (hasOrgParams) {
            setOrganizationForm(prev => ({
                ...prev,
                ...(orgParams.name && { name: orgParams.name }),
                ...(orgParams.vatNumber && { vatNumber: orgParams.vatNumber }),
                ...(orgParams.invoiceEmail && { invoiceEmail: orgParams.invoiceEmail }),
                ...(orgParams.attention && { attention: orgParams.attention }),
                ...(orgParams.street && { street: orgParams.street }),
                ...(orgParams.city && { city: orgParams.city }),
                ...(orgParams.zip && { zip: orgParams.zip }),
                ...(orgParams.country && { country: orgParams.country }),
            }));

            // If organization name is provided via URL, set attention field unless manually edited
            if (orgParams.name && !attentionManuallyEdited) {
                setOrganizationForm(prev => ({
                    ...prev,
                    attention: orgParams.name
                }));
            }
        }

        // Update user form if any parameters are provided
        const hasUserParams = Object.values(userParams).some(value => value !== null);
        if (hasUserParams) {
            setUserForm(prev => ({
                ...prev,
                ...(userParams.firstName && { firstName: userParams.firstName }),
                ...(userParams.lastName && { lastName: userParams.lastName }),
                ...(userParams.email && { email: userParams.email }),
            }));
        }

        // Handle discount code parameter
        const discountCodeParam = searchParams.get('discount') || searchParams.get('discountCode') || searchParams.get('promoCode');
        if (discountCodeParam) {
            setDiscountCode(discountCodeParam);
        }        // Handle plan selection parameter
        const planParam = searchParams.get('plan') || searchParams.get('tier');
        if (planParam && availablePlans.length > 0) {
            // Try to find plan by lookup key first (e.g., tier1_monthly, tier2_yearly)
            const planByLookupKey = findPriceByLookupKey(planParam);
            if (planByLookupKey) {
                setSelectedPlan(planByLookupKey.productId as 'tier1' | 'tier2');
                setSelectedPriceId(planByLookupKey.priceId);
            } else {
                // Fallback to old format for backward compatibility
                const normalizedPlan = planParam.toLowerCase();
                if (normalizedPlan === 'tier1' || normalizedPlan === 'tier2' || normalizedPlan === '1' || normalizedPlan === '2') {
                    const plan = normalizedPlan === '1' ? 'tier1' : normalizedPlan === '2' ? 'tier2' : normalizedPlan as 'tier1' | 'tier2';
                    setSelectedPlan(plan);
                    // Don't set price_id here as we don't know the billing interval
                }
            }
        }
    }, [searchParams, attentionManuallyEdited, availablePlans]);// Handle setup intent return from Stripe
    useEffect(() => {
        const setupIntentClientSecret = searchParams.get('setup_intent_client_secret');
        if (setupIntentClientSecret) {
            // When returning from Stripe, ensure we're on the payment step
            setCurrentStep(RegistrationStep.PaymentMethod);
            setSetupIntentClientSecret(setupIntentClientSecret);
            validateSetupIntent(setupIntentClientSecret);
        }
    }, [searchParams]);// Redirect to home if user is already authenticated
    useEffect(() => {
        if (user) {
            navigate("/");
        }
    }, [user, navigate]);    // Fetch available plans when component mounts
    useEffect(() => {
        fetchAvailablePlans();
    }, []);

    // Handle URL parameters after plans are loaded
    useEffect(() => {
        if (availablePlans.length > 0) {
            const planParam = searchParams.get('plan') || searchParams.get('tier');
            if (planParam) {
                // Try to find plan by lookup key first (e.g., tier1_monthly, tier2_yearly)
                const planByLookupKey = findPriceByLookupKey(planParam);
                if (planByLookupKey) {
                    setSelectedPlan(planByLookupKey.productId as 'tier1' | 'tier2');
                    setSelectedPriceId(planByLookupKey.priceId);
                }
            }
        }
    }, [availablePlans, searchParams]);// Find price_id based on lookup key
    const findPriceByLookupKey = (lookupKey: string): { productId: string; priceId: string } | null => {
        const plan = availablePlans.find(plan => plan.price_attrs.lookup_key === lookupKey);
        if (plan) {
            // Determine tier based on product name
            const isAgencyPack = plan.product_name.toLowerCase().includes('agency');
            const productId = isAgencyPack ? 'tier2' : 'tier1';
            return { productId, priceId: plan.price_id };
        }
        return null;
    };

    // Handle plan selection
    const handlePlanSelect = (productId: string, priceId: string) => {
        setSelectedPlan(productId as 'tier1' | 'tier2');
        setSelectedPriceId(priceId);
    };// Organization details form state
    const [organizationForm, setOrganizationForm] = useState(() => 
        loadFromStorage(STORAGE_KEYS.ORGANIZATION_FORM, {
            name: "",
            vatNumber: "",
            invoiceEmail: "",
            attention: "",
            street: "",
            city: "",
            zip: "",
            country: "Belgium",
        })
    );

    // User details form state
    const [userForm, setUserForm] = useState({
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        confirmPassword: "",
        acceptTerms: false,
    });

    // Password validation criteria
    const hasLowercase = /[a-z]/.test(userForm.password);
    const hasUppercase = /[A-Z]/.test(userForm.password);
    const hasDigit = /[0-9]/.test(userForm.password);
    const hasMinLength = userForm.password.length >= 6;
    const isPasswordValid = hasLowercase && hasUppercase && hasDigit && hasMinLength;    // Add password match validation
    const passwordsMatch = userForm.password === userForm.confirmPassword;

    // Persist organization form to localStorage
    useEffect(() => {
        saveToStorage(STORAGE_KEYS.ORGANIZATION_FORM, organizationForm);
    }, [organizationForm]);

    // Persist plan selection to localStorage
    useEffect(() => {
        saveToStorage(STORAGE_KEYS.PLAN_SELECTION, {
            selectedPlan,
            selectedPriceId,
            discountCode
        });
    }, [selectedPlan, selectedPriceId, discountCode]);

    // Persist attention manually edited state
    useEffect(() => {
        saveToStorage(STORAGE_KEYS.ATTENTION_MANUALLY_EDITED, attentionManuallyEdited);
    }, [attentionManuallyEdited]);

    // Persist current step (but don't persist UserDetails step for security)
    useEffect(() => {
        if (currentStep !== RegistrationStep.UserDetails) {
            saveToStorage(STORAGE_KEYS.CURRENT_STEP, currentStep);
        }
    }, [currentStep]);

    // Handle organization form changes
    const handleOrgChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setOrganizationForm(prev => ({
            ...prev,
            [name]: value
        }));

        // If changing organization name, update attention field unless manually edited
        if (name === "name") {
            if (!attentionManuallyEdited) {
                setOrganizationForm(prev => ({
                    ...prev,
                    attention: value
                }));
            }
        }

        // If changing the attention field, mark it as manually edited
        if (name === "attention") {
            setAttentionManuallyEdited(true);
        }
    };

    // Handle country selection
    const handleCountryChange = (value: string) => {
        setOrganizationForm(prev => ({
            ...prev,
            country: value
        }));
    };

    // Handle user form changes
    const handleUserChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setUserForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Toggle password visibility
    const togglePasswordVisibility = () => {
        setShowPassword(prev => !prev);
    };
    // Handle terms and conditions checkbox
    const handleTermsChange = (checked: boolean) => {
        setUserForm(prev => ({
            ...prev,
            acceptTerms: checked
        }));
    };    // Validate setup intent from Stripe
  	const validateSetupIntent = async (clientSecret: string) => {
		try {
			const stripe = await stripePromise;
			if (!stripe) {
				throw new Error('Stripe not loaded');
			}

			const { setupIntent, error } = await stripe.retrieveSetupIntent(clientSecret);
			
			if (error) {
				throw new Error(error.message);
			}			if (setupIntent && setupIntent.status === 'succeeded') {
				setPaymentMethodCreated(true);
				setSetupIntentId(setupIntent.id);
				// Stay on payment step to show success message and completion button
			} else if (setupIntent && setupIntent.status === 'processing') {
				// Payment method is being processed
			} else {
				throw new Error('Setup intent not completed');
			}		} catch (error) {
			console.error("Setup intent validation error:", error);
			setSetupIntentClientSecret(null);
			setPaymentMethodCreated(false);
		}
	};
	// Handle successful payment method creation
	const handlePaymentMethodSuccess = (setupIntentId: string) => {
		setPaymentMethodCreated(true);
		setSetupIntentId(setupIntentId);
		
		// Clear URL parameters to clean up the interface
		const url = new URL(window.location.href);
		url.searchParams.delete('setup_intent_client_secret');
		url.searchParams.delete('setup_intent');
		window.history.replaceState({}, '', url.toString());
	};

	// Handle payment method errors
	const handlePaymentMethodError = (error: string) => {
		console.error("Payment method error:", error);
		setSetupIntentClientSecret(null);
		setPaymentMethodCreated(false);
		setSetupIntentId(null);
	};

    // Fetch available plans from the edge function
    const fetchAvailablePlans = async () => {
        setIsLoadingPlans(true);
        try {
            const response = await fetch(`${SUPABASE_URL}/functions/v1/register-to-digger?command=get-products`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch plans');
            }

            const data = await response.json();
            if (data) {
                setAvailablePlans(data);
            }        } catch (error: any) {
            console.error("Error fetching plans:", error);
        } finally {
            setIsLoadingPlans(false);
        }
    };

    // Create Setup Intent for payment method creation
    const createSetupIntent = async () => {
        if (!selectedPriceId) {
            return;
        }

        setIsCreatingSetupIntent(true);

        try {
            // Create setup intent using the register-to-digger function
            const response = await fetch(`${SUPABASE_URL}/functions/v1/register-to-digger?command=create-setup-intent`, {
                method: 'POST'
            });

            if (!response.ok) {
                throw new Error('Failed to create setup intent');
            }

            const result = await response.json();
            if (result.setup_intent?.client_secret) {
                setSetupIntentClientSecret(result.setup_intent.client_secret);
                setSetupIntentId(result.setup_intent.id);
                setCurrentStep(RegistrationStep.PaymentMethod);
            } else {
                throw new Error('No setup intent client secret received');
            }        } catch (error) {
            console.error("Setup intent creation error:", error);
        } finally {
            setIsCreatingSetupIntent(false);
        }
    };// Go to next step
    const handleNextStep = () => {
        if (currentStep === RegistrationStep.OrganizationDetails) {
            // Validate organization form
            if (!organizationForm.name) {
                return;
            }
            if (!organizationForm.vatNumber) {
                return;
            }
            if (!organizationForm.invoiceEmail) {
                return;
            }
            if (!organizationForm.street || !organizationForm.city || !organizationForm.zip) {
                return;
            }
        } else if (currentStep === RegistrationStep.PlanSelection) {
            // Validate plan selection
            if (!selectedPriceId) {
                return;
            }
        } else if (currentStep === RegistrationStep.UserDetails) {
            // Validate user form before final submission
            if (!userForm.firstName || !userForm.lastName) {
                return;
            }
            if (!userForm.email) {
                return;
            }
            if (!isPasswordValid) {
                return;
            }
            if (!passwordsMatch) {
                return;
            }
            if (!userForm.acceptTerms) {
                return;
            }
        }

        setCurrentStep(prev => prev + 1);
    };

    // Go to previous step
    const handlePrevStep = () => {
        setCurrentStep(prev => prev - 1);
    };  	// Submit the registration form
	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		// Validate that payment method has been created
		if (!paymentMethodCreated || !setupIntentId) {
			return;
		}

		// Validate required form data
		if (!selectedPriceId) {
			return;
		}

		// Validate user form
		if (!userForm.firstName || !userForm.lastName) {
			return;
		}
		if (!userForm.email) {
			return;
		}
		if (!isPasswordValid) {
			return;
		}
		if (!passwordsMatch) {
			return;
		}
		if (!userForm.acceptTerms) {
			return;
		}

		setIsSubmitting(true);

		try {
			// Prepare the registration data with setup intent ID
			const registrationData = {
				organization: {
					...organizationForm
				},
				user: {
					firstName: userForm.firstName,
					lastName: userForm.lastName,
					email: userForm.email,
					password: userForm.password
				},
				payment: {
					setup_intent_id: setupIntentId,
					price_id: selectedPriceId,
					...(discountCode && { discount_code: discountCode })
				}
			};

			// Make the registration request to the edge function
			const response = await fetch(`${SUPABASE_URL}/functions/v1/register-to-digger?command=complete`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(registrationData),
			});

			// Check if the request was successful
			if (!response.ok) {
				const errorData = await response.json();
				console.error('Registration error response:', errorData);
				throw new Error(errorData.message || `Registration failed (${response.status})`);
			}

			const result = await response.json();
			//console.log('Registration successful:', result);			// Clear registration data from localStorage
			clearRegistrationStorage();

			// Registration successful

			// Auto-login the user
			const { data, error } = await supabase.auth.signInWithPassword({
				email: userForm.email,
				password: userForm.password,
			});

			if (error) {
				console.error('Auto-login error:', error);
				navigate("/auth");
				return;
			}

			if (data.user) {
				// Redirect to the dashboard
				navigate("/");
			}

		} catch (error) {
			console.error("Registration error:", error);
			const errorMessage = error instanceof Error ? error.message : "Registration failed. Please try again.";
		} finally {
			setIsSubmitting(false);
		}
	};

    // Render the appropriate step
    const renderStep = () => {
        switch (currentStep) {
            case RegistrationStep.OrganizationDetails:
                return (
                    <>
                        <CardHeader className="text-center pb-8">
                            <div className="flex items-center justify-center mb-4">
                                <div className="bg-digger-blue/10 p-3 rounded-sm">
                                    <Building className="h-8 w-8 text-digger-blue" />
                                </div>
                            </div>
                            <CardTitle className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">
                                {t("register.organization.title") || "Organization Details"}
                            </CardTitle>
                            <CardDescription className="text-base text-gray-600 dark:text-gray-300 mt-2">
                                {t("register.organization.description") || "Tell us about your company to get started"}
                            </CardDescription>
                        </CardHeader>
                        
                        <CardContent className="space-y-6 px-6 md:px-8">
                            {/* Company Information */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                                    <Shield className="h-5 w-5 text-digger-blue" />
                                    {t("register.organization.companyInformation") || "Company Information"}
                                </h3>
                                
                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="name" className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                            {t("register.organization.name") || "Organization Name"} *
                                        </Label>
                                        <Input
                                            id="name"
                                            name="name"
                                            value={organizationForm.name}
                                            onChange={handleOrgChange}
                                            placeholder={t("register.organization.namePlaceholder") || "Enter your organization name"}
                                            className="h-11 text-base"
                                            required
                                        />
                                    </div>
                                    
                                    <div className="space-y-2">
                                        <Label htmlFor="vatNumber" className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                            {t("register.organization.vatNumber") || "VAT Number"} *
                                        </Label>
                                        <Input
                                            id="vatNumber"
                                            name="vatNumber"
                                            value={organizationForm.vatNumber}
                                            onChange={handleOrgChange}
                                            placeholder={t("register.organization.vatNumberPlaceholder") || "Enter VAT number"}
                                            className="h-11 text-base"
                                            required
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="invoiceEmail" className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                            {t("register.organization.invoiceEmail") || "Email Address for Invoicing"} *
                                        </Label>
                                        <Input
                                            id="invoiceEmail"
                                            name="invoiceEmail"
                                            type="email"
                                            value={organizationForm.invoiceEmail}
                                            onChange={handleOrgChange}
                                            placeholder={t("register.organization.invoiceEmailPlaceholder") || "Enter email for invoicing"}
                                            className="h-11 text-base"
                                            required
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Billing Address */}
                            <div className="space-y-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                                    <CreditCard className="h-5 w-5 text-digger-blue" />
                                    {t("register.organization.billingAddress") || "Billing Address"}
                                </h3>

                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="attention" className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                            {t("register.organization.attention") || "Attention"}
                                        </Label>
                                        <Input
                                            id="attention"
                                            name="attention"
                                            value={organizationForm.attention}
                                            onChange={handleOrgChange}
                                            placeholder={t("register.organization.attentionPlaceholder") || "Enter attention (defaults to organization name)"}
                                            className="h-11 text-base"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="street" className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                            {t("register.organization.street") || "Street Address"} *
                                        </Label>
                                        <Input
                                            id="street"
                                            name="street"
                                            value={organizationForm.street}
                                            onChange={handleOrgChange}
                                            placeholder={t("register.organization.streetPlaceholder") || "Enter street address"}
                                            className="h-11 text-base"
                                            required
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="city" className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                                {t("register.organization.city") || "City"} *
                                            </Label>
                                            <Input
                                                id="city"
                                                name="city"
                                                value={organizationForm.city}
                                                onChange={handleOrgChange}
                                                placeholder={t("register.organization.cityPlaceholder") || "Enter city"}
                                                className="h-11 text-base"
                                                required
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="zip" className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                                {t("register.organization.zip") || "ZIP Code"} *
                                            </Label>
                                            <Input
                                                id="zip"
                                                name="zip"
                                                value={organizationForm.zip}
                                                onChange={handleOrgChange}
                                                placeholder={t("register.organization.zipPlaceholder") || "Enter ZIP code"}
                                                className="h-11 text-base"
                                                required
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className="space-y-2">
                                        <Label htmlFor="country" className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                            {t("register.organization.country") || "Country"}
                                        </Label>
                                        <Select
                                            value={organizationForm.country}
                                            onValueChange={handleCountryChange}
                                        >
                                            <SelectTrigger className="h-11 text-base">
                                                <SelectValue placeholder={t("register.organization.countryPlaceholder") || "Select a country"} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="Belgium">Belgium</SelectItem>
                                                <SelectItem value="France">France</SelectItem>
                                                <SelectItem value="Germany">Germany</SelectItem>
                                                <SelectItem value="Netherlands">Netherlands</SelectItem>
                                                <SelectItem value="Luxembourg">Luxembourg</SelectItem>
                                                <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                                                <SelectItem value="Other">Other</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                        
                        <CardFooter className="flex justify-end px-6 md:px-8 pb-8">
                            <Button 
                                onClick={handleNextStep} 
                                className="flex items-center gap-2 bg-digger-blue hover:bg-digger-blue-light text-white px-8 py-3 h-12 text-base font-medium rounded-sm shadow-lg hover:shadow-xl transition-all duration-200"
                            >
                                {t("register.nextStep") || "Continue"}
                                <ChevronRight className="h-5 w-5" />
                            </Button>
                        </CardFooter>
                    </>
                );            case RegistrationStep.PlanSelection:
                return (
                    <>
                        <CardHeader className="text-center pb-8">
                            <div className="flex items-center justify-center mb-4">
                                <div className="bg-digger-blue/10 p-3 rounded-sm">
                                    <Zap className="h-8 w-8 text-digger-blue" />
                                </div>
                            </div>
                            <CardTitle className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">
                                {t("register.payment.title") || "Choose Your Plan"}
                            </CardTitle>
                            <CardDescription className="text-base text-gray-600 dark:text-gray-300 mt-2">
                                {t("register.payment.description") || "Select the plan that best fits your organization's needs"}
                            </CardDescription>
                            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-sm">
                                <p className="text-xs text-blue-700 font-medium flex items-center gap-2">
                                    <Shield className="h-4 w-4" />
                                    {t("register.payment.securityNote") || "Start with a 30-day free trial — no charge until your trial period ends."}
                                </p>
                            </div>
                            <div className="mt-3 flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-sm">
                                <Sparkles className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
                                <div>
                                    <p className="text-sm font-medium text-green-900">
                                        {t("register.freeTrial.title") || "30-day free trial included"}
                                    </p>
                                    <p className="text-sm text-green-700">
                                        {t("register.freeTrial.description") || "Your card will not be charged until your trial ends. Cancel anytime before then."}
                                    </p>
                                </div>
                            </div>

                        </CardHeader>                        
                        
                        <CardContent className="space-y-6 px-6 md:px-8">
                            <PlanSelection
                                availablePlans={availablePlans}
                                isLoading={isLoadingPlans}
                                selectedPriceId={selectedPriceId}
                                discountCode={discountCode}
                                onPlanSelect={handlePlanSelect}
                                onDiscountCodeChange={setDiscountCode}
                                onValidate={createSetupIntent}
                                isCreating={isCreatingSetupIntent}
                            />
                        </CardContent>
                        
                        <CardFooter className="flex justify-between px-6 md:px-8 pb-8">
                            <Button 
                                variant="outline" 
                                onClick={handlePrevStep}
                                className="flex items-center gap-2 px-6 py-3 h-12 text-base font-medium rounded-sm border-2 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200"
                            >
                                <ChevronLeft className="h-5 w-5" />
                                {t("register.back") || "Back"}
                            </Button>
                        </CardFooter>
                    </>
                );            case RegistrationStep.PaymentMethod:
                return (
                    <>
                        <CardHeader className="text-center pb-8">
                            <div className="flex items-center justify-center mb-4">
                                <div className="bg-digger-blue/10 p-3 rounded-sm">
                                    <CreditCard className="h-8 w-8 text-digger-blue" />
                                </div>
                            </div>
                            <CardTitle className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">
                                {t("register.paymentMethod.title") || "Payment Method"}
                            </CardTitle>
                            <CardDescription className="text-base text-gray-600 dark:text-gray-300 mt-2">
                                {t("register.paymentMethod.description") || "Add your payment method to complete registration"}
                            </CardDescription>
                            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-sm">
                                <p className="text-xs text-blue-700 font-medium flex items-center gap-2">
                                    <Shield className="h-4 w-4" />
                                    {t("register.paymentMethod.securityNote") || "Start with a 30-day free trial — no charge until your trial period ends."}
                                </p>
                            </div>
                        </CardHeader>
                        
                        <CardContent className="space-y-6 px-6 md:px-8">
                            <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-sm">
                                <Sparkles className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
                                <div>
                                    <p className="text-sm font-medium text-green-900">
                                        {t("register.freeTrial.title") || "30-day free trial included"}
                                    </p>
                                    <p className="text-sm text-green-700">
                                        {t("register.freeTrial.description") || "Your card will not be charged until your trial ends. Cancel anytime before then."}
                                    </p>
                                </div>
                            </div>

                            {setupIntentClientSecret && !paymentMethodCreated && (
                                <Elements stripe={stripePromise} options={{ clientSecret: setupIntentClientSecret }}>
                                    <RegistrationSetupForm 
                                        onSuccess={handlePaymentMethodSuccess}
                                        onError={handlePaymentMethodError}
                                    />
                                </Elements>
                            )}
                            
                            {!setupIntentClientSecret && !paymentMethodCreated && (
                                <div className="text-center p-8">
                                    <div className="text-gray-600 dark:text-gray-300 mb-6">
                                        {t("register.paymentMethod.goBackMessage") || "Please go back and select a plan to set up your payment method."}
                                    </div>
                                    <Button 
                                        variant="outline" 
                                        onClick={handlePrevStep}
                                        className="flex items-center gap-2 px-6 py-3 h-12 text-base font-medium rounded-sm border-2 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200"
                                    >
                                        <ChevronLeft className="h-5 w-5" />
                                        {t("register.paymentMethod.backToPlanSelection") || "Back to Plan Selection"}
                                    </Button>
                                </div>
                            )}
                            
                            {paymentMethodCreated && (
                                <div className="bg-white dark:bg-gray-800 p-8 rounded-sm border border-gray-200 dark:border-gray-700 text-center shadow-sm">
                                    <div className="flex items-center justify-center mb-4">
                                        <div className="bg-digger-blue/10 p-3 rounded-sm">
                                            <Check className="h-8 w-8 text-digger-blue" />
                                        </div>
                                    </div>
                                    <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
                                        {t("register.paymentMethod.successTitle") || "Payment Method Added Successfully!"}
                                    </h3>
                                    <p className="text-gray-600 dark:text-gray-300 mb-4 leading-relaxed">
                                        {t("register.paymentMethod.successMessage") || "Your payment method has been securely saved. You can now proceed to create your user account."}
                                        <br />
                                        <span className="text-sm text-gray-500 dark:text-gray-400">
                                            {t("register.paymentMethod.chargingNote") || "Your 30-day free trial starts when you create your account. You will only be charged after the trial period."}
                                        </span>
                                    </p>
                                </div>
                            )}
                        </CardContent>
                        
                        <CardFooter className="flex justify-between px-6 md:px-8 pb-8">
                            <Button 
                                variant="outline" 
                                onClick={handlePrevStep}
                                className="flex items-center gap-2 px-6 py-3 h-12 text-base font-medium rounded-sm border-2 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200"
                            >
                                <ChevronLeft className="h-5 w-5" />
                                {t("register.back") || "Back"}
                            </Button>
                            
                            <div className="flex gap-3">
                                {!paymentMethodCreated && setupIntentClientSecret && (
                                    <Button 
                                        variant="outline"
                                        onClick={() => {
                                            setSetupIntentClientSecret(null);
                                            setPaymentMethodCreated(false);
                                            setSetupIntentId(null);
                                            handlePrevStep();
                                        }}
                                        className="px-6 py-3 h-12 text-base font-medium rounded-sm border-2 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200"
                                    >
                                        {t("register.paymentMethod.startOver", "Start Over")}
                                    </Button>
                                )}
                                {paymentMethodCreated && (
                                    <Button 
                                        onClick={handleNextStep}
                                        className="flex items-center gap-2 bg-digger-blue hover:bg-digger-blue-light text-white px-8 py-3 h-12 text-base font-medium rounded-sm shadow-lg hover:shadow-xl transition-all duration-200"
                                    >
                                        {t("register.nextStep") || "Continue"}
                                        <ChevronRight className="h-5 w-5" />
                                    </Button>
                                )}
                            </div>
                        </CardFooter>
                    </>
                );            case RegistrationStep.UserDetails:
                return (
                    <>
                        <CardHeader className="text-center pb-8">
                            <div className="flex items-center justify-center mb-4">
                                <div className="bg-digger-blue/10 p-3 rounded-sm">
                                    <Users className="h-8 w-8 text-digger-blue" />
                                </div>
                            </div>
                            <CardTitle className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">
                                {t("register.user.title") || "Create Your Account"}
                            </CardTitle>
                            <CardDescription className="text-base text-gray-600 dark:text-gray-300 mt-2">
                                {t("register.user.description") || "Final step: Create your user account and complete registration"}
                            </CardDescription>
                            <div className="mt-6 p-3 bg-orange-25 border border-orange-100 rounded-sm">
                                <p className="text-xs text-orange-600 font-normal flex items-center gap-2">
                                    <CreditCard className="h-3 w-3" />
                                    {t("register.user.chargingWarning", "Your 30-day free trial starts when you create your account. You will not be charged until after the trial period.")}
                                </p>
                            </div>
                        </CardHeader>
                        
                        <CardContent className="space-y-6 px-6 md:px-8">
                            {/* Personal Information */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                                    <Users className="h-5 w-5 text-digger-blue" />
                                    Personal Information
                                </h3>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="firstName" className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                            {t("register.user.firstName") || "First Name"} *
                                        </Label>
                                        <Input
                                            id="firstName"
                                            name="firstName"
                                            value={userForm.firstName}
                                            onChange={handleUserChange}
                                            placeholder={t("register.user.firstNamePlaceholder") || "Enter your first name"}
                                            className="h-11 text-base"
                                            required
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="lastName" className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                            {t("register.user.lastName") || "Last Name"} *
                                        </Label>
                                        <Input
                                            id="lastName"
                                            name="lastName"
                                            value={userForm.lastName}
                                            onChange={handleUserChange}
                                            placeholder={t("register.user.lastNamePlaceholder") || "Enter your last name"}
                                            className="h-11 text-base"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="email" className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                        {t("register.user.email") || "Email Address"} *
                                    </Label>
                                    <Input
                                        id="email"
                                        name="email"
                                        type="email"
                                        value={userForm.email}
                                        onChange={handleUserChange}
                                        placeholder={t("register.user.emailPlaceholder") || "Enter your email address"}
                                        className="h-11 text-base"
                                        required
                                    />
                                </div>
                            </div>

                            {/* Security */}
                            <div className="space-y-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                                    <Shield className="h-5 w-5 text-digger-blue" />
                                    Security
                                </h3>
                                
                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="password" className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                            {t("register.user.password") || "Password"} *
                                        </Label>
                                        <div className="relative">
                                            <Input
                                                id="password"
                                                name="password"
                                                type={showPassword ? "text" : "password"}
                                                autoComplete="new-password"
                                                value={userForm.password}
                                                onChange={handleUserChange}
                                                placeholder={t("register.user.passwordPlaceholder") || "Create a password"}
                                                className="h-11 text-base pr-12"
                                                required
                                            />
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-sm"
                                                onClick={togglePasswordVisibility}
                                            >
                                                {showPassword ?
                                                    <EyeOff className="h-4 w-4" /> :
                                                    <Eye className="h-4 w-4" />
                                                }
                                            </Button>
                                        </div>
                                    </div>
                                    
                                    <div className="space-y-2">
                                        <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                            {t("register.user.confirmPassword") || "Confirm Password"} *
                                        </Label>
                                        <Input
                                            id="confirmPassword"
                                            name="confirmPassword"
                                            type={showPassword ? "text" : "password"}
                                            autoComplete="new-password"
                                            value={userForm.confirmPassword}
                                            onChange={handleUserChange}
                                            placeholder={t("register.user.confirmPasswordPlaceholder") || "Confirm your password"}
                                            className="h-11 text-base"
                                            required
                                        />
                                        {userForm.confirmPassword && !passwordsMatch && (
                                            <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                                                <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                                                {t("register.user.passwordsDoNotMatch") || "Passwords do not match"}
                                            </p>
                                        )}
                                    </div>
                                    
                                    {/* Password Requirements */}
                                    <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-sm">
                                        <p className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">
                                            {t("register.user.passwordRequirements") || "Password requirements:"}
                                        </p>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                            <div className={`flex items-center gap-2 text-sm ${
                                                hasLowercase ? "text-green-600" : "text-gray-500 dark:text-gray-400"
                                            }`}>
                                                <CheckCircle className={`h-4 w-4 ${hasLowercase ? "text-green-500" : "text-gray-400"}`} />
                                                {t("register.user.passwordLowercase") || "At least one lowercase letter"}
                                            </div>
                                            <div className={`flex items-center gap-2 text-sm ${
                                                hasUppercase ? "text-green-600" : "text-gray-500 dark:text-gray-400"
                                            }`}>
                                                <CheckCircle className={`h-4 w-4 ${hasUppercase ? "text-green-500" : "text-gray-400"}`} />
                                                {t("register.user.passwordUppercase") || "At least one uppercase letter"}
                                            </div>
                                            <div className={`flex items-center gap-2 text-sm ${
                                                hasDigit ? "text-green-600" : "text-gray-500 dark:text-gray-400"
                                            }`}>
                                                <CheckCircle className={`h-4 w-4 ${hasDigit ? "text-green-500" : "text-gray-400"}`} />
                                                {t("register.user.passwordNumber") || "At least one number"}
                                            </div>
                                            <div className={`flex items-center gap-2 text-sm ${
                                                hasMinLength ? "text-green-600" : "text-gray-500 dark:text-gray-400"
                                            }`}>
                                                <CheckCircle className={`h-4 w-4 ${hasMinLength ? "text-green-500" : "text-gray-400"}`} />
                                                {t("register.user.passwordMinLength") || "At least 6 characters"}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Terms and Conditions */}
                            <div className="flex items-start space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                                <Checkbox
                                    id="acceptTerms"
                                    checked={userForm.acceptTerms}
                                    onCheckedChange={handleTermsChange}
                                    className="mt-1"
                                    required
                                />
                                <Label htmlFor="acceptTerms" className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
                                    {t("register.user.acceptTerms") || "I accept the"}{" "}
                                    <a
                                        href="https://digger-ai.be/terms"
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-digger-blue hover:underline font-medium"
                                    >
                                        {t("register.user.termsLink") || "Terms and Conditions"}
                                    </a>
                                </Label>
                            </div>
                        </CardContent>
                        
                        <CardFooter className="flex justify-between px-6 md:px-8 pb-8">
                            <Button 
                                type="button" 
                                variant="outline" 
                                onClick={handlePrevStep} 
                                className="flex items-center gap-2 px-6 py-3 h-12 text-base font-medium rounded-sm border-2 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200"
                            >
                                <ChevronLeft className="h-5 w-5" />
                                {t("register.previousStep") || "Back"}
                            </Button>
                            
                            <Button
                                type="button"
                                onClick={handleSubmit}
                                disabled={!isPasswordValid || !userForm.acceptTerms || !passwordsMatch || isSubmitting}
                                className="flex items-center gap-2 bg-digger-blue hover:bg-digger-blue-light text-white px-8 py-3 h-12 text-base font-medium rounded-sm shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSubmitting ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        {t("register.user.creatingAccount", "Creating Account...")}
                                    </>
                                ) : (
                                    <>
                                        {t("register.complete") || "Complete Registration"}
                                    </>
                                )}
                            </Button>
                        </CardFooter>
                    </>
                );
        }
    };
    return (
        <TooltipProvider>
            <div className="min-h-screen w-full bg-gradient-to-br from-digger-light-gray via-white to-digger-light-gray flex flex-col items-center justify-center p-4 md:p-6 lg:p-8">
                {/* Language Selector */}
                <div className="absolute top-4 right-4 z-10">
                    <LanguageSelector />
                </div>

                {/* Main Container */}
                <div className="w-full max-w-4xl mx-auto">
                {/* Header Section */}
                <div className="text-center mb-8">
                    <div className="flex justify-center mb-6">
                        <div className="flex items-center gap-3">
                            <div className="w-16 h-16 md:w-20 md:h-20 rounded-sm flex items-center justify-center overflow-hidden">
                                <img src="/img/NewDiggerIcon.png" alt="Digger" className="w-full h-full object-contain" />
                            </div>
                            <div className="flex items-baseline gap-1">
                                <span className="text-4xl md:text-5xl font-black tracking-tight text-foreground">
                                    digger
                                </span>
                                <span className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-[#5a7ebf] inline-block" />
                            </div>
                        </div>
                    </div>
                    
                    <div className="max-w-2xl mx-auto">
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                            {t("register.welcomeTitle") || "Welcome to Digger"}
                        </h1>
                        <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
                            {t("register.welcomeMessage") || "We're excited to have you join us! Let's get your account set up and start finding perfect matches."}
                        </p>
                    </div>
                </div>

                {/* Progress Steps */}
                <div className="mb-8">
                    <div className="grid grid-cols-4 gap-4 max-w-3xl mx-auto px-4">
                        {[
                            { step: RegistrationStep.OrganizationDetails, label: t("register.steps.organization") || "Organization", icon: Building },
                            { step: RegistrationStep.PlanSelection, label: t("register.steps.plan") || "Plan", icon: Zap },
                            { step: RegistrationStep.PaymentMethod, label: t("register.steps.payment") || "Payment", icon: CreditCard },
                            { step: RegistrationStep.UserDetails, label: t("register.steps.user") || "User Details", icon: Users }
                        ].map((item, index, arr) => {
                            const Icon = item.icon;
                            const isActive = currentStep === item.step;
                            const isCompleted = currentStep > item.step;
                            
                            return (
                                <div key={index} className="flex flex-col items-center relative">
                                    <div className="flex items-center justify-center w-full mb-3">
                                        <div className={`flex items-center justify-center rounded-sm z-10 transition-all duration-300 ${
                                            isActive 
                                                ? "bg-digger-blue text-white shadow-lg scale-110" 
                                                : isCompleted 
                                                    ? "bg-green-500 text-white shadow-md" 
                                                    : "bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                                        } w-12 h-12 md:w-14 md:h-14 text-lg md:text-xl font-semibold border-2 border-white shadow-sm`}>
                                            {isCompleted ? <CheckCircle className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                                        </div>
                                    </div>
                                    <div className="min-h-[40px] flex items-center justify-center w-full">
                                        <span className={`text-xs md:text-sm font-medium text-center transition-colors duration-300 ${
                                            isActive ? 'text-digger-blue' : isCompleted ? 'text-green-600' : 'text-gray-500 dark:text-gray-400'
                                        }`}>
                                            {item.label}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                </div>

                {/* Main Content Card */}
                <div className="card-glass shadow-xl border-0">
                    {renderStep()}
                </div>

                {/* Footer */}
                <div className="text-center mt-8 text-sm md:text-base text-gray-600 dark:text-gray-300">
                    {t("register.alreadyHaveAccount") || "Already have an account?"} {" "}
                    <a href="/auth" className="text-digger-blue font-semibold hover:underline transition-colors">
                        {t("register.signIn") || "Sign in"}
                    </a>
                </div>
            </div>
        </div>
        </TooltipProvider>
    );
};

export default RegisterPage;